import { useState } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";

const submitAddr = "https://readdy.ai/api/form/da2k59aamq5jpe1hrum0";

const topics = ["漫画推荐", "内容合作", "意见反馈", "其他问题"];

export default function Contact() {
  usePageMeta({
    title: "联系我们 - 羞羞漫画韩国",
    description:
      "羞羞漫画韩国联系我们页面，欢迎提交漫画推荐、内容合作与意见反馈。我们认真对待每一位漫友的声音，共同让更多好作品被看见。",
  });

  const [formError, setFormError] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "success">("idle");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // honeypot 检查
    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    setSubmitting(true);
    setFormError("");

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "website_alt" && String(value).trim()) {
        payload.append(key, String(value));
      }
    });

    try {
      const res = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || "";
      const isSpam = /spam/i.test(responseText) || /spam/i.test(serverMsg);

      if (!res.ok || !parsed?.code || parsed.code !== "OK" || isSpam) {
        setFormStatus("idle");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }
      setFormStatus("success");
      form.reset();
    } catch {
      setFormError("网络异常，请检查网络后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="w-full bg-background-50">
      <section className="border-b border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">联系我们</span>
          </nav>
          <h1 className="mt-4 font-display text-3xl font-black text-foreground-950 md:text-4xl">联系我们</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            有想推荐的韩国漫画、内容合作或任何建议，欢迎通过下面的表单告诉我们。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="font-display text-xl font-black text-foreground-950">联系方式</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们通常会在 1-2 个工作日内回复。漫友的每一份推荐，我们都会认真评估并纳入收录范围。
            </p>
            <ul className="mt-7 space-y-4">
              <li className="flex items-center gap-3 rounded-lg bg-background-100 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
                  <i className="ri-mail-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">邮箱</p>
                  <p className="text-sm font-medium text-foreground-950">hi@manhwa.example.com</p>
                </div>
              </li>
              <li className="flex items-center gap-3 rounded-lg bg-background-100 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-700">
                  <i className="ri-time-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">回复时间</p>
                  <p className="text-sm font-medium text-foreground-950">周一至周五 9:00 - 18:00</p>
                </div>
              </li>
              <li className="flex items-center gap-3 rounded-lg bg-background-100 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-900">
                  <i className="ri-chat-smile-line" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">欢迎</p>
                  <p className="text-sm font-medium text-foreground-950">漫友投稿 · 编辑合作 · 版权相关咨询</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-xl bg-background-100 p-6 md:p-8"
              noValidate
            >
              <h2 className="font-display text-xl font-black text-foreground-950">给我们留言</h2>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm font-medium text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="请输入你的称呼"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground-800">
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="example@email.com"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="topic" className="mb-2 block text-sm font-medium text-foreground-800">
                  需求类型
                </label>
                <select
                  id="topic"
                  name="topic"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
                >
                  {topics.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5">
                <label htmlFor="message" className="mb-2 block text-sm font-medium text-foreground-800">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="想推荐的漫画、合作意向或任何建议…"
                  className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
              </div>

              {/* 反垃圾蜜罐 */}
              <input
                type="text"
                name="website_alt"
                data-honeypot="true"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {formError && (
                <p className="mt-4 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800" role="alert">
                  {formError}
                </p>
              )}
              {formStatus === "success" && (
                <p className="mt-4 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900" role="status">
                  提交成功，感谢你的留言，我们会尽快与你联系。
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
                    提交留言
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ContactPage schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系我们 - 羞羞漫画韩国",
            description: "羞羞漫画韩国联系方式与留言表单。",
            inLanguage: "zh-CN",
            url: "https://shushmanga.github.io/contact",
          }),
        }}
      />
    </main>
  );
}